import BooleanType from "../enums/boolean-type";
import ClockType from "../enums/clock-type";
import FieldLength from "../enums/field-length";
import GoalPost from "../enums/goal-post";
import KickoffSkip from "../enums/kickoff-skip";
import TeamLoss from "../enums/team-loss";
import ParticipationStyle from "../enums/participation-style";

// `description` is the one-sentence definition surfaced by the info icon on
// each field and by the printable definitions sheet (PSTATS-1267). Every
// setting the form renders needs one; the CFL and Indoor form data spread this
// object, so they inherit these definitions unless they override the entry.
//
// `visibleWhen` ({ key, values }, PSTATS-1269) makes a field depend on another
// setting: the form shows it only while the setting at `key` holds one of
// `values`. A list of them shows the field while any one holds (PSTATS-1268,
// for flag's Overtime Starting Yardline). Hidden values are kept and saved, the
// UI skips a hidden field's validation, and the definitions sheet lists the
// setting whatever the current values - its definition says when it is hidden.
//
// `section` draws a field under another section's heading without moving its
// config key (PSTATS-1267's Field Areas; here OT Time Control and OT Period
// Length under Clock, after Running Clock, which sorts as order 1).
const customFormData = {
    general: {
        title: "Game information",
        downs: {
            // Title matches what fromCamelCase already produced, so the label
            // is unchanged - the entry exists to carry the definition.
            title: "Downs",
            description:
                "Default number of downs before the ball is turned over automatically, barring a first down yard to gain."
        },
        endZoneLength: {
            title: "End Zone Length (yds)",
            description:
                "Length of the end zone, north to south, used to calculate kick distance and yards earned in the end zone."
        },
        includeEndZoneYardage: {
            title: "Show End Zone Yards",
            options: Object.values(BooleanType),
            description:
                "Renders an additional field component to select end zone yards when yards earned on returns in the end zone are tracked."
        },
        fieldLength: {
            title: "Field Length (yds)",
            options: Object.values(FieldLength).filter(
                ({ code }) => code % 2 === 0
            ),
            description:
                "Distance in yards north and south on a given field of play, not including the end zone."
        },
        fieldWidth: {
            title: "Field Width (yds)",
            description: "Distance in yards east to west."
        },
        // TODO: this property shouldn't be here. This is part of the flag config.
        firstDownFlagStyle: {
            disabled: true
        },
        firstDownYards: {
            disabled: true
        },
        minutesByPeriod: {
            title: "Period Length (min)",
            description: "Total time of a given period, such as a quarter."
        },

        periodsInRegulation: {
            title: "Number of Periods",
            description:
                "Total number of periods to end before the game is final or overtime proceeds."
        },
        redZoneLength: {
            title: "Red Zone Length (yds)",
            description:
                "Distance to the goal line where down and distance becomes 'and Goal'."
        },
        timeoutsPerHalf: {
            title: "Timeouts Per Half",
            description: "Total number of allotted timeouts per team each half."
        },
        otTimeouts: {
            title: "OT Timeouts",
            description:
                "Total number of allotted timeouts per team in each overtime period, none carried over from regulation; 0 leaves timeouts unavailable in overtime."
        },
        zoneLength: {
            title: "Zone Length (yds)",
            disabled: true
        },
        // Stored in General, drawn under Clock (section, order). Not
        // editableAfterStart: locked once the game starts, like the rest of
        // General.
        otTimeControl: {
            title: "OT Time Control",
            options: Object.values(BooleanType),
            section: "clock",
            order: 3,
            description: "Determines if time is tracked during overtime."
        },
        otMinutesByPeriod: {
            title: "OT Period Length (min)",
            min: 1,
            section: "clock",
            order: 4,
            visibleWhen: { key: "general.otTimeControl", values: [true] },
            description:
                "Total time of each overtime period while OT Time Control is on."
        },
        autoFairCatchTb: {
            title: "Fair Catch Touchback",
            options: Object.values(BooleanType),
            description:
                "Places the ball on the touchback yardline, given a fair catch."
        },
        rushType: {
            title: "Rush Type",
            options: Object.values(BooleanType),
            description:
                "Renders an additional step when entering a rush play to indicate the direction of the run."
        },
        passType: {
            title: "Pass Type",
            options: Object.values(BooleanType),
            description:
                "Renders an additional step when entering a pass play to indicate the direction of the pass."
        },
        // Legacy keys — clock settings live in the clock section now, but
        // configs saved before the move may still carry them in general.
        clockType: {
            disabled: true
        },
        runningClock: {
            disabled: true
        },
        spikeTeamAttOnly: {
            title: "Spike as Team Attempt Only",
            options: Object.values(BooleanType),
            description:
                "Determines whether a quarterback spike is counted as a pass attempt."
        },
        touchdownsCountAsFirstDowns: {
            title: "Touchdowns Count as 1st Downs",
            options: Object.values(BooleanType),
            description:
                "Given a touchdown, a first down is added automatically."
        },
        teamLossOnly: {
            title: "Team Loss Only",
            options: Object.values(TeamLoss),
            multiselect: true,
            description:
                "Determines if a quarterback loses rushing yards on a kneel down and/or sack, or only impacts team net total."
        },
        collectStatsOnPat: {
            title: "Collect Additional Stats on PAT",
            options: Object.values(BooleanType),
            description:
                "Determines if statistics earned on 2-point conversions and PATs are tracked, outside of kicking ratio and percentage."
        },
        collectStatsForOnSideKicks: {
            title: "Collect Stats For On Side Kicks",
            options: Object.values(BooleanType),
            description:
                "Determines if kicking and return stats are tracked on onside kicks."
        },
        teamZoneQty: {
            disabled: true
        },
        promptToSkipKickoff: {
            title: "Prompt To Forego Kickoff",
            options: Object.values(KickoffSkip),
            multiselect: true,
            description:
                "Allows a kickoff to be skipped, with the ball placed at the drive starting yardline."
        },
        participationStyle: {
            title: "Participation Style",
            options: Object.values(ParticipationStyle),
            description:
                "Determines whether participating players are set by the crew or collected automatically from the players named in each play."
        }
    },
    clock: {
        title: "Clock",
        // editableAfterStart: clock settings stay editable while the rest of
        // the form is locked mid-game (the save guard only allows clock-
        // section changes through).
        clockType: {
            title: "Clock Type",
            options: Object.values(ClockType),
            editableAfterStart: true,
            description:
                "Determines whether the clock time ascends or descends."
        },
        // No options: plain booleans render as a Switch in the form.
        runningClock: {
            title: "Running Clock",
            editableAfterStart: true,
            description:
                "BETA: Allows the game clock to run automatically, prefilling play time from the clock when entry begins."
        }
    },
    spots: {
        title: "Default Yardlines",
        KOTouchback: {
            title: "KO Touchback",
            description:
                "Default yardline where the ball is placed following a kickoff then touchback."
        },
        PATTry: {
            title: "PAT Try",
            description:
                "Default yardline where the ball is snapped on a point after attempt."
        },
        safetyKO: {
            title: "Safety KO",
            description:
                "Spot where the ball is placed for a kick following a safety."
        },
        driveStartingYardline: {
            title: "Drive Starting Yardline",
            description:
                "Spot where the ball is placed following a foregone kickoff."
        },
        touchbackYardline: {
            title: "Touchback Yardline",
            description:
                "Spot where the ball is placed following a non-kickoff touchback."
        },
        otKickoffs: {
            title: "OT Kickoffs",
            options: Object.values(BooleanType),
            description:
                "Determines if kickoffs follow scores in OT; while on, Overtime Starting Yardline is hidden."
        },
        overtimeStartingYardline: {
            title: "Overtime Starting Yardline",
            visibleWhen: { key: "spots.otKickoffs", values: [false] },
            description:
                "Spot where the ball is placed for new drives during overtime; hidden while OT Kickoffs is on."
        },
        kickoff: {
            title: "Kickoff Yardline",
            description:
                "Default yardline where the ball is placed given a kickoff."
        },
        otherTouchback: {
            disabled: true
        },
        rougeYardline: {
            title: "Rouge Yardline",
            description:
                "Spot where the ball is placed following a rouge, measured from the receiving team's own goal line."
        },
        spotsPatScoreOption1: {
            disabled: true
        },
        spotsPatScoreOption2: {
            disabled: true
        },
        spotsPatScoreOption3: {
            disabled: true
        },
        goalPostLocation: {
            title: "Goal Post Location",
            options: Object.values(GoalPost),
            description:
                "Identifies where the goal post is located in an end zone, which factors into field goal and kick PAT yards."
        },
        missedFieldGoalMinSpot: {
            disabled: true
        },
        missedFieldGoalSpot: {
            disabled: true
        }
    },
    scoring: {
        title: "Scoring Information",
        defensivePAT: {
            title: "Defensive PAT",
            description:
                "Number of points awarded for a PAT attempt that is returned to the end zone by the defense."
        },
        kickPAT: {
            title: "Kick PAT",
            description:
                "Number of points awarded for a successful point after attempt."
        },
        runPAT: {
            title: "Pass/Run PAT",
            description:
                "Number of points awarded for a conversion point after attempt."
        },
        touchdown: {
            title: "Touchdown Score",
            description: "Number of points awarded for a touchdown."
        },
        safety: {
            title: "Safety Score",
            description: "Number of points awarded for a safety."
        },
        rougePoints: {
            title: "Rouge Score",
            description: "Number of points awarded for a rouge."
        },
        // Indoor-only scoring keys. The season and event config merges replace
        // the whole scoring section rather than merging it, so a config saved
        // under Indoor carries these into every other football ruleset. Left
        // untitled, fromCamelCase rendered dropKickPAT as "Drop Kick P A T";
        // these titles fix that. Deliberately no description: they are not
        // settings of this ruleset, so they get a correct label but stay off
        // its definitions sheet. Indoor's own form data replaces each entry.
        dropKickFieldGoal: {
            title: "Drop Kick Field Goal"
        },
        dropKickPAT: {
            title: "Drop Kick PAT"
        },
        deuce: {
            title: "Deuce Score"
        },
        fieldGoal: {
            disabled: true
        },
        playSummary: {
            disabled: true
        },
        scoringPatScoreOption1: {
            disabled: true
        },
        scoringPatScoreOption2: {
            disabled: true
        },
        scoringPatScoreOption3: {
            disabled: true
        }
    }
};

export default customFormData;
